/**
 * Custom Price Module
 *
 * Keeps PLP custom prices in sync with selected variants and automatic discounts.
 */

(function() {
  'use strict';

  function getMoneyFormat() {
    // Non-API money format global (exposed in layout/theme.liquid). Survives the
    // removal of storefront-api-config; keep __storefrontApi as legacy fallback.
    if (window.__moneyFormat) {
      return window.__moneyFormat;
    }
    var storefrontConfig = window.__storefrontApi;
    return storefrontConfig && storefrontConfig.moneyFormat ? storefrontConfig.moneyFormat : '${{amount}}';
  }

  /**
   * Format money amount using the shop money format.
   * @param {number} amountInCents
   * @param {string} moneyFormat
   * @returns {string}
   */
  function formatMoneyFromCents(amountInCents, moneyFormat) {
    var amount = amountInCents / 100;
    var formatted = amount.toFixed(2);
    var parts = formatted.split('.');
    var dollars = parts[0];
    var cents = parts[1];
    var withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + cents;
    var withDot = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + cents;
    var rounded = Math.round(amount).toString();
    var roundedWithComma = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var roundedWithDot = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var roundedWithApostrophe = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    var withApostrophe = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + cents;

    return moneyFormat
      .replace('{{amount_with_apostrophe_separator}}', withApostrophe)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', roundedWithComma)
      .replace('{{amount_no_decimals_with_apostrophe_separator}}', roundedWithApostrophe)
      .replace('{{amount_no_decimals}}', roundedWithDot)
      .replace('{{ amount }}', withDot)
      .replace('{{ amount_with_comma_separator }}', withComma)
      .replace('{{amount}}', withDot);
  }

  function parseConfig(customPriceEl) {
    if (!customPriceEl) return null;

    var configStr = customPriceEl.getAttribute('data-custom-price-config');
    if (!configStr) return null;

    try {
      return JSON.parse(configStr);
    } catch (e) {
      return null;
    }
  }

  function getCardPrice(card, attributeName) {
    return parseInt(card.getAttribute(attributeName) || '0', 10) || 0;
  }

  function syncCardVariantData(card, variantId, productPrice, compareAtPrice) {
    if (!card) return;

    if (variantId != null) {
      card.setAttribute('data-variant-id', String(variantId));
    }

    if (productPrice != null) {
      card.setAttribute('data-product-price', String(productPrice));
    }

    card.setAttribute('data-compare-at-price', String(compareAtPrice || 0));

    var customPriceEl = card.querySelector('.custom-price');
    if (customPriceEl && variantId != null) {
      customPriceEl.setAttribute('data-custom-price-variant', String(variantId));
    }
  }

  /**
   * Replace the .custom-price markup inside a <product-price> host using new HTML
   * coming from a variant change. Works for both PLP cards and PDP — caller is
   * responsible for resolving the <product-price> element before calling.
   */
  function replaceCustomPriceMarkup(productPriceEl, htmlDocument) {
    if (!productPriceEl || !htmlDocument) return;

    var currentCustomPrice = productPriceEl.querySelector('.custom-price');
    var nextCustomPrice = htmlDocument.querySelector('product-price .custom-price');

    if (currentCustomPrice && !nextCustomPrice) {
      currentCustomPrice.remove();
      return;
    }

    if (!nextCustomPrice) return;

    if (currentCustomPrice) {
      currentCustomPrice.replaceWith(nextCustomPrice);
      return;
    }

    var currentPriceContainer = productPriceEl.querySelector('[ref="priceContainer"]');

    if (currentPriceContainer) {
      currentPriceContainer.insertAdjacentElement('afterend', nextCustomPrice);
    } else {
      productPriceEl.appendChild(nextCustomPrice);
    }
  }

  function getEffectiveBasePrice(card, customPriceEl, config, overrideBasePriceInCents) {
    if (typeof overrideBasePriceInCents === 'number' && !isNaN(overrideBasePriceInCents)) {
      return overrideBasePriceInCents;
    }

    var productPrice = getCardPrice(card, 'data-product-price');
    var compareAtPrice = getCardPrice(card, 'data-compare-at-price');

    if (config.accumulates === true) {
      // When an automatic discount is active, gco-automatic-discounts.js already
      // dispatches custom-price:sync with the discounted price as
      // overrideBasePriceInCents (handled above). Without an active automatic
      // discount, the base price is just the current product price.
      return productPrice;
    }

    if (compareAtPrice > productPrice) {
      return compareAtPrice;
    }

    return productPrice;
  }

  /**
   * Apply final discounted price to the .custom-price element and update its base attribute.
   * Shared by PLP and PDP recalc paths — only the basePrice calculation differs between them.
   */
  function paintCustomPrice(customPriceEl, basePrice, config) {
    var percentage = parseFloat(config.percentage) || 0;
    percentage = Math.max(0, Math.min(99, percentage));
    var finalPrice = Math.round(basePrice * (1 - percentage / 100));
    var priceValueEl = customPriceEl.querySelector('.custom-price__value');

    if (priceValueEl) {
      priceValueEl.textContent = formatMoneyFromCents(finalPrice, getMoneyFormat());
    }

    customPriceEl.setAttribute('data-custom-price-base', String(basePrice));
  }

  /**
   * Recalculate custom price for a specific card.
   * @param {HTMLElement} card
   * @param {number} [overrideBasePriceInCents]
   */
  function recalculateForCard(card, overrideBasePriceInCents) {
    if (!card) return;

    var customPriceEl = card.querySelector('.custom-price');
    var config = parseConfig(customPriceEl);

    if (!customPriceEl || !config) return;

    var basePrice = getEffectiveBasePrice(card, customPriceEl, config, overrideBasePriceInCents);
    if (!basePrice) return;

    paintCustomPrice(customPriceEl, basePrice, config);
  }

  function recalculateForPDPHost(host, overrideBasePriceInCents) {
    if (!host) return;

    var customPriceEl = host.querySelector('.custom-price');
    var config = parseConfig(customPriceEl);

    if (!customPriceEl || !config) return;

    var galleryContainer = document.querySelector('.gallery-main-container[data-variant-id]');
    var variantId = galleryContainer ? galleryContainer.getAttribute('data-variant-id') : null;

    if (!variantId) return;

    var basePrice;
    if (typeof overrideBasePriceInCents === 'number' && !isNaN(overrideBasePriceInCents)) {
      basePrice = overrideBasePriceInCents;
    } else {
      var priceContainer = host.querySelector('[ref="priceContainer"]');
      if (!priceContainer) return;

      var priceEl = priceContainer.querySelector('.price');
      if (!priceEl) return;

      var priceText = priceEl.textContent || '';
      basePrice = parsePriceTextToCents(priceText);
    }

    if (!basePrice) return;

    paintCustomPrice(customPriceEl, basePrice, config);
    customPriceEl.setAttribute('data-custom-price-variant', variantId);
  }

  function parsePriceTextToCents(priceText) {
    if (!priceText) return 0;

    var cleaned = priceText.replace(/[^\d.,]/g, '');
    var parts = cleaned.split(/[.,]/);
    var cents = 0;

    if (parts.length === 1) {
      cents = parseInt(parts[0], 10) * 100;
    } else if (parts.length === 2) {
      var lastPart = parts[1];
      if (lastPart.length === 2) {
        cents = parseInt(parts[0].replace(/[,]/g, ''), 10) * 100 + parseInt(lastPart, 10);
      } else if (lastPart.length === 3) {
        // Separator de miles — COP $41.990 = 41990 pesos = 4199000 "cents"
        cents = parseInt(parts[0] + lastPart, 10) * 100;
      } else {
        cents = parseInt(parts[0].replace(/[,]/g, ''), 10) * 100;
      }
    } else if (parts.length >= 3) {
      var mainPart = parts.slice(0, -1).join('').replace(/[,]/g, '');
      var decimalPart = parts[parts.length - 1];
      if (decimalPart.length === 2) {
        cents = parseInt(mainPart, 10) * 100 + parseInt(decimalPart, 10);
      } else {
        cents = parseInt(mainPart + decimalPart, 10) * 100;
      }
    }

    return cents || 0;
  }

  function handleDiscountSync(event) {
    var detail = event.detail;
    if (!detail || !detail.card || typeof detail.effectivePrice !== 'number') return;

    recalculateForCard(detail.card, detail.effectivePrice);
  }

  function handlePDPSync(event) {
    var detail = event.detail;
    if (!detail || !detail.host || typeof detail.effectivePrice !== 'number') return;

    recalculateForPDPHost(detail.host, detail.effectivePrice);
  }

  function handlePLPVariantChange(event) {
    var detail = event.detail;
    if (!detail || !detail.card) return;

    syncCardVariantData(detail.card, detail.variantId, detail.price, detail.compareAtPrice);
    recalculateForCard(detail.card);
  }

  function handleThemeVariantUpdate(event) {
    var detail = event.detail;
    var target = event.target;
    var card = target && target.closest ? target.closest('product-card') : null;

    if (detail && detail.resource && card) {
      syncCardVariantData(card, detail.resource.id, detail.resource.price, detail.resource.compare_at_price);
      var cardProductPriceEl = card.querySelector('product-price');
      if (cardProductPriceEl) {
        replaceCustomPriceMarkup(cardProductPriceEl, detail.html);
      }
      recalculateForCard(card);
      return;
    }

    var productPrice = target && target.closest ? target.closest('product-price') : null;

    if (detail && detail.resource && productPrice) {
      replaceCustomPriceMarkup(productPrice, detail.html);
      recalculateForPDPHost(productPrice);
    }
  }

  function syncAllCustomPrices() {
    var customPriceNodes = document.querySelectorAll('.custom-price');

    for (var i = 0; i < customPriceNodes.length; i++) {
      var card = customPriceNodes[i].closest('product-card');
      if (!card) continue;

      recalculateForCard(card);
    }
  }

  function handleWishlistHydrate(event) {
    var detail = event && event.detail;
    if (!detail || (detail.action !== 'hydrate' && detail.action !== 'add')) return;
    syncAllCustomPrices();
  }

  function init() {
    document.addEventListener('custom-price:sync', handleDiscountSync);
    document.addEventListener('custom-price:sync-pdp', handlePDPSync);
    document.addEventListener('plp:variant-changed', handlePLPVariantChange);
    // Horizon 4.0.0 retiro `variant:update` en favor del evento estandar `shopify:product:select`.
    // Este archivo no es un modulo ES: se escucha por nombre literal, sin import map.
    document.addEventListener(
      'shopify:product:select',
      function (event) {
        if (!event.promise) return;
        event.promise
          .then(function (result) {
            var d = result && result.detail;
            if (!d) return;
            handleThemeVariantUpdate({ detail: d, target: event.target });
          })
          .catch(function () {});
      },
      true
    );
    window.addEventListener('wishlist:change', handleWishlistHydrate);

    syncAllCustomPrices();
    syncAllPDPCustomPrices();
  }

  function syncAllPDPCustomPrices() {
    var customPriceNodes = document.querySelectorAll('product-price .custom-price');

    for (var i = 0; i < customPriceNodes.length; i++) {
      var host = customPriceNodes[i].closest('product-price');
      if (!host) continue;

      recalculateForPDPHost(host);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CustomPriceModule = {
    recalculateForCard: recalculateForCard,
    recalculateForPDPHost: recalculateForPDPHost,
    syncAllCustomPrices: syncAllCustomPrices,
    syncAllPDPCustomPrices: syncAllPDPCustomPrices
  };

})();
